import * as React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ErrorBoundary caught an error]:", error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-6">
          <div className="max-w-md w-full bg-background border rounded-2xl p-6 shadow-lg text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-950 text-red-600 flex items-center justify-center mx-auto">
              <AlertCircle className="w-6 h-6" />
            </div>
            <h2 className="text-lg font-bold text-foreground">Đã Xảy Ra Lỗi Hiển Thị</h2>
            <p className="text-xs text-muted-foreground leading-relaxed font-mono bg-muted p-3 rounded-lg text-left overflow-auto max-h-36">
              {this.state.error?.message || "Lỗi giao diện không xác định."}
            </p>
            <div className="flex justify-center gap-3 pt-2">
              <Button
                variant="outline"
                className="gap-2 text-xs"
                onClick={() => {
                  try {
                    localStorage.clear();
                  } catch {}
                  window.location.reload();
                }}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Xóa Bộ Nhớ Đệm & Tải Lại
              </Button>
              <Button
                className="gap-2 text-xs"
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.hash = "#/print";
                  window.location.reload();
                }}
              >
                Tải Lại Trang In
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface SectionErrorBoundaryProps {
  /** Short label for the region, e.g. "Bước Dữ Liệu". */
  label?: string;
  children: React.ReactNode;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Inline error boundary for a single region of the UI (a step, the editor, the
 * preview). Unlike the full-screen ErrorBoundary, it renders a compact fallback
 * card so the rest of the app keeps working.
 */
export class SectionErrorBoundary extends React.Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[SectionErrorBoundary:${this.props.label ?? "region"}]`, error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="flex flex-col items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-center"
        >
          <AlertCircle className="h-5 w-5 text-destructive" />
          <p className="text-sm font-semibold text-foreground">
            {this.props.label ?? "Khu vực này"} gặp lỗi hiển thị
          </p>
          <p className="max-w-md text-xs text-muted-foreground">
            {this.state.error?.message || "Lỗi giao diện không xác định."}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            <RefreshCw className="h-3 w-3" />
            Thử lại
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
